// libraries
import React, { useState } from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import Link from '/react4xp/components/Link'
import MasonryGrid from '/react4xp/components/MasonryGrid'
import IdeaCard, { IdeaCardProps } from '/react4xp/components/Idea/IdeaCard'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils.js'

// svg
import { UxArrowRight } from '/react4xp/components/Svg/Ux/Arrow'

export interface AllIdeasProps {
    section?: {
        bgColor?: string
        paddingTop?: 'big' | 'small' | 'smaller' | 'medium' | 'none'
        paddingBottom?: 'big' | 'small' | 'smaller' | 'medium' | 'none'
        id?: string
        className?: string
    }
    isWhite?: boolean
    title?: string
    totalText?: {
        before?: string
        of?: string
        text?: string
    }
    viewAll?: {
        text?: string
        href?: string
        external?: boolean
    }
    items?: IdeaCardProps[]
    howManyItemsToShow?: number
    loadMore?: {
        text?: string
        leftAligned?: boolean
        isWhite?: boolean
    }
}

export default function AllIdeas({
    section,
    isWhite,
    title,
    totalText,
    viewAll,
    items = [],
    howManyItemsToShow,
    loadMore
}: AllIdeasProps) {
    
    const [itemsToShow, setItemsToShow] = useState(howManyItemsToShow)
    const displayItems = items.slice(0, itemsToShow)

    const loadMoreItems = () => {
        setItemsToShow(prev => Math.min(prev + howManyItemsToShow, items.length))
    }

    return (
        <Section
            bgColor={section?.bgColor}
            paddingTop={section?.paddingTop}
            paddingBottom={section?.paddingBottom}
            className={clsx('all-ideas', section?.className)}
            id={section?.id}
        >
            <div className='container'>

                {isNonWhitespaceString(title) && (
                    <h2 className={clsx(
                        'h2 all-ideas__title',
                        isWhite && 'white'
                    )}>
                        {title}
                    </h2>
                )}

                <div className='all-ideas__details'>

                    {totalText && (
                        <div className='total'>
                            <p className={clsx(isWhite && 'white')}>
                                {totalText?.before} {displayItems.length} {totalText?.of} {items.length} {totalText?.text}
                            </p>
                        </div>
                    )}

                    {isNonWhitespaceString(viewAll?.text) && (
                        <Link
                            href={viewAll?.href}
                            text={viewAll?.text}
                            iconPosition='right'
                            icon={<UxArrowRight />}
                            isWhite={isWhite}
                        />
                    )}

                </div>

                {items.length > 0 ? (
                    <div className='all-ideas__grid'>

                        <MasonryGrid
                            items={displayItems}
                            columnGutter={20}
                            maxColumnCount={3}
                            breakpointCols={{
                                default: 3,
                                1200: 2,
                                676: 1
                            }}
                            columnWidth={275}
                            overscanBy={99}
                            render={({ data }, i) => (
                                <IdeaCard
                                    key={data.id || `item-${i}`}
                                    {...data}
                                />
                            )}
                        />

                        {items.length > itemsToShow && (
                            <div className={clsx(
                                'load-more',
                                loadMore?.leftAligned && 'load-more--left-aligned'
                            )}>
                                <Link
                                    text={loadMore?.text}
                                    onClick={loadMoreItems}
                                    iconPosition='right'
                                    icon={<UxArrowRight />}
                                    noMotionMobile
                                    isWhite={loadMore?.isWhite}
                                />
                            </div>
                        )}

                    </div>
                ) : (
                    <div className='all-ideas__no-results'>
                        <p className={clsx(
                            'h3',
                            isWhite && 'white'
                        )}>
                            Ingen idéer funnet
                        </p>
                    </div>
                )}
                
            </div>
        </Section>
    )
}